import React, { useState } from 'react';
import { breakpointForWidth, BREAKPOINT_LABELS } from '../tokens';

export const MIN_FRAME_WIDTH = 320;

/**
 * Drag handle on the right edge of the builder's page frame (edit canvas or
 * live preview) — resizes the whole page to any width, like a browser's
 * responsive mode. The parent must be the frame element itself (position:
 * relative); the frame's own parent is the space it may grow into.
 *
 * onWidthChange(px) fires continuously while dragging. The caller keeps the
 * width and decides which Desktop/Tablet/Mobile bucket that falls into.
 */
export default function FrameWidthHandle({ onWidthChange, maxWidth: maxWidthProp }) {
    const [liveWidth, setLiveWidth] = useState(null);

    function handlePointerDown(e) {
        e.preventDefault();
        e.stopPropagation();
        const frame = e.currentTarget.parentElement;
        const startX = e.clientX;
        const startWidth = frame.getBoundingClientRect().width;
        const container = frame.parentElement;
        const style = window.getComputedStyle(container);
        const available = container.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
        // By default the page can grow into the space available; a preview that
        // scrolls sideways passes its own (larger) cap.
        const maxWidth = maxWidthProp || Math.max(MIN_FRAME_WIDTH, available);
        // A centred frame grows on BOTH sides (so 2x the mouse travel keeps its
        // edge under the cursor); one wider than the space it sits in is left-
        // aligned and only grows to the right.
        const factor = startWidth > available + 1 ? 1 : 2;

        const onMove = (ev) => {
            const next = Math.round(Math.min(maxWidth, Math.max(MIN_FRAME_WIDTH, startWidth + factor * (ev.clientX - startX))));
            setLiveWidth(next);
            onWidthChange(next);
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
            setLiveWidth(null);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
    }

    return (
        <>
            <span className="lb-frame-handle" title="Drag to resize the page" onPointerDown={handlePointerDown} />
            {liveWidth != null && (
                <span className="lb-frame-readout">{liveWidth}px · {BREAKPOINT_LABELS[breakpointForWidth(liveWidth)]}</span>
            )}
        </>
    );
}
